import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './PracticeQuestion.css';
import { AuthContext } from '../../App';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faHeart } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function PracticeQuestion() {
    const location = useLocation();
    const navigate = useNavigate();
    const { isSignedIn } = useContext(AuthContext);
    const { isPremiumUser } = useContext(AuthContext);

    const { questionNumber, questionTitle } = location.state;

    const [likes, setLikes] = useState(0);
    const [dislikes, setDislikes] = useState(0);
    const [answer, setAnswer] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [liked, setLiked] = useState(false);
    const [disliked, setDisliked] = useState(false);
    const [addedToList, setAddedToList] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const questionId = questionNumber;

    const handleLockIconClick = () => {
        navigate('/premium');
    };

    const handleAction = (action) => {
        if (!isSignedIn) {
            navigate('/signin');
            return;
        }
        action();
    };

    const handleTextareaChange = (e) => {
        if (!isSignedIn) {
            navigate('/signin');
            return;
        }
        setAnswer(e.target.value);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const formData = new FormData();
        formData.append('question_id', questionId);
        formData.append('question', questionTitle);
        formData.append('answer', answer);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}${isPremiumUser ? '/premium-submit-answer' : '/submit-answer'}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
            });
            setFeedback(response.data);
            setErrorMessage(''); // Clear any previous error message
        } catch (error) {
            const errorCode = error.response.data.code;
            if (errorCode === 'LIMIT_REACHED') {
                const message = (
                    <div>
                        🌟 You've hit your limit of 5 free submissions. Unlock unlimited submissions and advanced features by subscribing{' '}
                        <span
                            style={{ cursor: 'pointer', color: '#FFA115', fontWeight: 'bold' }}
                            onClick={() => navigate('/premium')} // Navigate to the premium page onClick
                        >
                            Premium
                        </span>{' '}
                        now!
                    </div>
                );

                // Display the toast notification with the clickable link
                toast(message, {
                    autoClose: 10000, // Adjust the auto-close time as needed
                    closeOnClick: false, // Prevent toast from closing when clicking the content
                });
            } else if (errorCode === 'NO_WORD') {
                toast('Please type your answer before submit.', { autoClose: 5000, closeOnClick: false, });
            } else if (errorCode === 'MORE_WORD') {
                toast('Please answer with no more than 500 words.', { autoClose: 5000, closeOnClick: false, });
            } else {
                console.error('There has been a problem with your submission:', error);
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    useEffect(() => {
        const fetchLikeDislikeStatus = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_user_like_dislike_status?question_id=${questionId}`, {
                    withCredentials: true,
                });

                setLiked(response.data.liked);
                setDisliked(response.data.disliked);
            } catch (error) {
                console.error('Error fetching like/dislike status:', error);
            }
        };

        if (isSignedIn) {
            fetchLikeDislikeStatus();
        } else {
            setLiked(false);
            setDisliked(false);
        }
    }, [isSignedIn, questionId]);

    useEffect(() => {
        const fetchLikeDislikeCount = async () => {
            try {
                // Adjust the endpoint if necessary to match your API for fetching like/dislike counts
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get_like_dislike_count?question_id=${questionId}`);

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const counts = await response.json();
                setLikes(counts.likes);
                setDislikes(counts.dislikes);
            } catch (error) {
                console.error('Error fetching like/dislike counts:', error);
            }
        };

        fetchLikeDislikeCount();
    }, [questionId]); // Dependency array ensures this only reruns if questionId changes

    const handleLike = async () => {
        const newLikedStatus = !liked;  // Toggle the current liked status
        await updateLikeDislike(newLikedStatus, false);  // Update based on the new status
        setLiked(newLikedStatus);  // Set the state based on the new status
        setDisliked(false);  // Ensure that dislike is set to false
    };

    const handleDislike = async () => {
        const newDislikedStatus = !disliked;  // Toggle the current disliked status
        await updateLikeDislike(false, newDislikedStatus);  // Update based on the new status
        setLiked(false);  // Ensure that like is set to false
        setDisliked(newDislikedStatus);  // Set the state based on the new status
    };

    const updateLikeDislike = async (like, dislike) => {
        try {
            const updateResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/update_like_dislike`, {
                question_id: questionId,
                like: like,
                dislike: dislike
            }, {
                withCredentials: true
            });

            if (updateResponse.status !== 200) {
                throw new Error(`Network response was not ok: ${updateResponse.status}`);
            }

            const countsResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_like_dislike_count?question_id=${questionId}`);

            setLikes(countsResponse.data.likes);
            setDislikes(countsResponse.data.dislikes);

        } catch (error) {
            console.error('There has been a problem with your update operation:', error);
        }
    };

    const handleAddToList = async () => {
        const action = addedToList ? 'remove' : 'add';
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/add_to_list`, {
                question_id: questionId,
                action: action
            }, {
                withCredentials: true,
            });
            setAddedToList(!addedToList); // Toggle the addedToList state based on the action taken
        } catch (error) {
            console.error('There has been a problem with your list operation:', error);
        }
    };

    const fetchUserList = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get_user_list`, {
                withCredentials: true
            });

            const isAdded = response.data.questions.some(q => q.id === questionId);
            setAddedToList(isAdded);
        } catch (error) {
            console.error('Error fetching user list:', error);
        }
    };

    useEffect(() => {
        if (isSignedIn) {
            fetchUserList();
        }
    }, [isSignedIn, questionId]);

    return (
        <div className="practice-question-container">
            <div className="question-header">
                <h1 className="question-title">{`Question ${questionNumber}: ${questionTitle}`}</h1>
            </div>
            <div className="question-interactions">
                <button onClick={() => handleAction(handleLike)}>
                    <FontAwesomeIcon
                        icon={faThumbsUp}
                        className={liked ? 'icon-clicked' : 'icon-default'}
                    /> {likes}
                </button>
                <button onClick={() => handleAction(handleDislike)}>
                    <FontAwesomeIcon
                        icon={faThumbsDown}
                        className={disliked ? 'icon-clicked' : 'icon-default'}
                    /> {dislikes}
                </button>
                <button onClick={() => handleAction(handleAddToList)}>
                    <FontAwesomeIcon
                        icon={faHeart}
                        className={addedToList ? 'icon-clicked' : 'icon-default'}
                    /> Add to List
                </button>
            </div>

            <div className="answer-input-container">
                <textarea
                    value={answer}
                    onChange={handleTextareaChange}
                    placeholder="Type your answer here..."
                    className="answer-input"
                />
                <div className="word-limit-container">
                    <span className="word-limit-text">Max 500 words</span>
                </div>
                <div className="button-container">
                    <button onClick={() => handleAction(handleSubmit)} className="submit-answer-button" disabled={isSubmitting}>
                        {isSubmitting ? "It is evaluating, please wait" : "Submit for Feedback"}
                    </button>
                </div>
                {errorMessage && <div className="error-message">{errorMessage}</div>}
            </div>

            {/* Feedback Section */}
            {feedback && (
                <div className="feedback-container">
                    <div className="feedback-score">
                        <span className="feedback-message">{feedback['Short feedback']}</span>
                        <div className="score-circle">{feedback.Score}</div>
                    </div>
                    <div className="feedback-text">
                        <strong>Feedback:</strong>
                        <p>{feedback.Feedback}</p>
                    </div>
                    <div className="enhanced-response">
                        <strong>Enhanced Response:</strong>
                        {feedback['Enhanced response'] ? (
                            <p>{feedback['Enhanced response']}</p>
                        ) : (
                            <p className="locked-response" onClick={handleLockIconClick}>🔒</p>
                        )}
                    </div>
                </div>
            )}
            <ToastContainer position="top-center" autoClose={10000} />
        </div>
    );
}

export default PracticeQuestion;
